import { formatPrice, maybe } from '../../common/util.js';
import { Picture } from './image.js';
import { memo } from 'preact/compat';
import dayjs from 'dayjs';
import { PublicDomain } from '../../common/data/domains.js';
import { ComputedProduct } from '../../common/data/products.js';
import { getProductPreviewImageUrl } from '../lib/images.js';
import { Icon } from './icons.js';

export const ProductCard = memo(function ProductCard({ product, domain, position }: {
	product: ComputedProduct;
	domain: PublicDomain;
	position: number;
}) {

	const preview = maybe(product.preview_image, getProductPreviewImageUrl);
	const shadow = (
		maybe(product.preview_image?.image, src => `/data/products/${src.replace('.png', '')}.shadow.png`)
		?? maybe(product.mask_image, src => `/data/product-masks/${src}.front.shadow.png`)
	);

	const pdS = product.pd_count ? 's' : '';
	const { name } = product;
	const url = product.info_url ?? `/product/${product.id}`;

	return <div
		class="product-card"
		style={{ '--product-aspect-ratio': product.image_size?.join(' / ') }}
		itemType="https://schema.org/Product"
		itemScope
	>

		<link itemProp="image" href={`https://${domain.host}${preview}`} />

		<div class="product-card__image">
			{!!preview && <a class="product-card__image-inner" href={url}>
				<Picture
					class="product-card__image-img"
					src={preview}
					loading={position < 16 ? 'eager' : 'lazy'}
					alt={name}
				/>
				{!!shadow && <Picture
					class="product-shadow"
					src={shadow}
					loading={position < 16 ? 'eager' : 'lazy'}
					aria-hidden="true"
				/>}
			</a>}
		</div>

		<div className="product-card__actions">
			<a className="product-card__action product-card__action--left" href={`/builder?product=${product.alias}`}>
				<Icon name="design-logo" aria-hidden={true} />
				<span>Design<span className="product-card__action-tmi">{' Now'}</span></span>
			</a>
			<a className="product-card__action product-card__action--right" href={url}>
				<Icon name="sizes" aria-hidden={true} />
				<span><span className="product-card__action-tmi">{'Product '}</span>Info</span>
			</a>
		</div>

		<div class="product-card__bottom">

			<a
				class="product-card__bottom-row product-card__bottom-row--name"
				title={name}
				href={url}
			>
				<span itemProp="name">{name}</span>
			</a>

			<meta itemProp="url" content={`https://${domain.host}${url}`} />

			{/*product.main_category && <p
					class="product-card__bottom-row"
					title={getCategoryName(product.main_category)}
				>{getCategoryName(product.main_category)}</p>*/}

			{product.pd_count && <p
				class="product-card__bottom-row"
				title={`Available ${product.templates_are_colors ? 'Color' : 'Design Template'}s`}
			>
				<span>{product.pd_count}</span>{' '}
				{product.templates_are_colors ? <span>Color{pdS}</span> : <span>Design<span className="product-card__bottom-row-tmi">{' Template'}</span>{pdS}</span>}
			</p>}
			<p
				class="product-card__bottom-row"
				itemProp="offers"
				itemType="https://schema.org/Offer"
				itemScope
			>
				<meta itemProp="availability" content="https://schema.org/InStock"/>
				<span itemProp="eligibleQuantity" itemType="https://schema.org/QuantitativeValue" itemScope>
					<meta itemProp="minValue" content={String(product.min_order_quantity || 1)} />
				</span>
				<span
					class="product-card__bottom-row"
					itemProp="priceSpecification"
					itemScope
					itemType="https://schema.org/UnitPriceSpecification"
				>
					<meta itemProp="price" content={String(product.price)} />
					<meta itemProp="priceCurrency" content="USD"/>
					{maybe(product.price_range, pr => <>
						<data itemProp="minPrice" value={String(pr[0])}>{formatPrice(pr[0])}</data>
						{' - '}
						<data itemProp="maxPrice" value={String(pr[1])}>{formatPrice(pr[1])}</data>
					</>)}
					{!product.price_range && maybe(product.price, formatPrice)}
				</span>
			</p>
			<p class="product-card__bottom-row">
				{maybe(
					product.min_order_quantity,
					moq => moq > 1 ? <span>{moq} Piece Minimum</span> : null,
				) ?? <span>No Minimum</span>}
				<span class="product-card__bottom-row-tmi">{' Order'}</span>
			</p>
			<p className="product-card__bottom-row">
				<span className="product-card__bottom-row-tmi">{'Lead Time: '}</span>
				{ dayjs().add((product.min_prod_days || 0) + 1, 'days').format('MMM D') } - { dayjs().add((product.max_prod_days || 0) + 1, 'days').format('MMM D') }
			</p>

		</div>

	</div>;
}, ({ product: a }, { product: b }) => (
	a.id == b.id
	&& a.name == b.name
	&& a.preview_image?.id == b.preview_image?.id
));
